import type {
  DatabaseSearchPage,
  DatabaseSearchRequest,
} from '@/models/DatabaseSearch'

const serverURL = import.meta.env.VITE_API_URL

export const INVALID_FILTERS_MESSAGE =
  "Some of your filters couldn't be used. Check your keywords and tags, or reset filters and try again."

/** A refusal the page shows as-is: no filters, no iwi, or a rate limit. */
export class DatabaseSearchError extends Error {
  status: number
  retryAfterSeconds: number | null

  constructor(message: string, status: number, retryAfterSeconds: number | null) {
    super(message)
    this.name = 'DatabaseSearchError'
    this.status = status
    this.retryAfterSeconds = retryAfterSeconds
  }
}

async function errorFrom(response: Response): Promise<DatabaseSearchError> {
  let detail = `HTTP error! status: ${response.status}`
  try {
    const body = await response.json()
    if (typeof body?.detail === 'string') detail = body.detail
  } catch {
    // Keep the status message when the body is not JSON.
  }
  // A 422 carries pydantic's field errors, not words meant for the user.
  if (response.status === 422) detail = INVALID_FILTERS_MESSAGE
  const retryAfter = Number(response.headers.get('Retry-After'))
  return new DatabaseSearchError(
    detail,
    response.status,
    Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : null,
  )
}

export async function searchDatabase(
  token: string,
  orgUuid: string,
  request: DatabaseSearchRequest,
): Promise<DatabaseSearchPage> {
  const response = await fetch(`${serverURL}/search/${orgUuid}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  })
  if (!response.ok) throw await errorFrom(response)
  return response.json()
}

export async function fetchSearchTags(
  token: string,
  orgUuid: string,
): Promise<string[]> {
  const response = await fetch(`${serverURL}/search/${orgUuid}/tags`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!response.ok) throw await errorFrom(response)
  return response.json()
}
